import { useEffect, useState } from "react";

import { sharedLocalStorage } from "~/features/storage/local.client";
import { useHydrated } from "./use-hydrated";

export const DEFAULT_APP_ID = "MISSING";

const androidAppIdRegex = new RegExp(
  "android-app:\\/\\/(no.vy.personnel.trainstaffassistant[\\w|.]*)",
);

const twaToTopasKjoretoyAppIdMap = new Map<string, string>([
  [
    "no.vy.personnel.trainstaffassistant.twa",
    "no.vy.topas.kjoretoy.app.prod.sso",
  ],
  [
    "no.vy.personnel.trainstaffassistant.twa.beta",
    "no.vy.topas.kjoretoy.app.beta",
  ],
  [
    "no.vy.personnel.trainstaffassistant.twa.stage",
    "no.vy.topas.kjoretoy.app.test",
  ],
]);

const twaToDriverModeAppIdMap = new Map<string, string>([
  ["no.vy.personnel.trainstaffassistant.twa", "no.vy.personnel.drivermode"],
  [
    "no.vy.personnel.trainstaffassistant.twa.beta",
    "no.vy.personnel.drivermode.beta",
  ],
  [
    "no.vy.personnel.trainstaffassistant.twa.stage",
    "no.vy.personnel.drivermode.beta",
  ],
]);

export const getAppIdFromReferrer = (referrer: string) => {
  if (androidAppIdRegex.test(referrer)) {
    const res = referrer.split(androidAppIdRegex);
    // Actual matches will split the referrer in 3 parts, where the middle one is the actual app id
    if (res.length === 3) {
      return res[1];
    }
  }
  return null;
};

export const useTwa = () => {
  const isHydrated = useHydrated();
  const [isTwa, setIsTwa] = useState(false);
  const [currentAppId, setCurrentAppId] = useState(DEFAULT_APP_ID);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    function updateState(
      storedAppId: string | null,
      localAppId: string | null,
    ) {
      if (localAppId && localAppId.length > 0) {
        // We should update the local storage
        setCurrentAppId(localAppId);
        setIsTwa(true);
        void sharedLocalStorage.androidAppId.set(localAppId);
      } else if (storedAppId && storedAppId.length > 0) {
        // We should use the stored id
        setCurrentAppId(storedAppId);
        setIsTwa(true);
      } else {
        // If both remote and local app id are blank we are almost certainly not in a TWA.
        setIsTwa(false);
      }
    }

    sharedLocalStorage.androidAppId
      .get()
      .then((value) =>
        updateState(value, getAppIdFromReferrer(document.referrer)),
      );
  }, [isHydrated]);

  const getIntentHref = (url: string) => {
    // Only format the url as an intent if we are in a TWA
    if (isTwa) {
      return `intent://#Intent;scheme=open-link;package=${currentAppId};S.url_to_open=${encodeURIComponent(
        url,
      )};end`;
    }

    // For iOS PWAs in standalone mode, use x-safari protocol to force opening in Safari
    // See: https://stackoverflow.com/questions/60267796/ios-pwa-how-to-open-external-link-on-mobile-default-safarinot-in-app-browser
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(display-mode: standalone)").matches &&
      /iPhone|iPad|iPod/.test(window.navigator.userAgent)
    ) {
      // Replace http:// or https:// with x-safari-http:// or x-safari-https://
      if (url.startsWith("https://") || url.startsWith("http://")) {
        return `x-safari-${url}`;
      }
    }

    // For regular browsers or other platforms, return unchanged
    return url;
  };

  const getTopasKjoretoyAppId = (): string => {
    const appId = twaToTopasKjoretoyAppIdMap.get(currentAppId);
    if (!appId) {
      console.log(
        `App ID ${currentAppId} did not map to any specific Topas Kjøretøy app id. Returning default value no.vy.topas.kjoretoy.app.prod.sso`,
      );
      return "no.vy.topas.kjoretoy.app.prod.sso";
    } else {
      return appId;
    }
  };

  const getDriverModeIntentHref = (): string => {
    let appId = twaToDriverModeAppIdMap.get(currentAppId);
    if (!appId) {
      console.log(
        `App ID ${currentAppId} did not map to any specific Driver Mode app id. Using default value no.vy.personnel.drivermode`,
      );
      appId = "no.vy.personnel.drivermode";
    }
    return `intent://#Intent;scheme=driver-mode;package=${appId};end;S.browser_fallback_url=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3D${appId}`;
  };

  const getAcknowledgeDayPlanIntentHref = () => {
    return `intent://#Intent;scheme=acknowledge-day-plan;package=${currentAppId};end`;
  };

  return {
    isTwa,
    getIntentHref,
    getTopasKjoretoyAppId,
    getDriverModeIntentHref,
    getAcknowledgeDayPlanIntentHref,
    currentAppId,
  };
};
